import { Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ActivatedRouteSnapshot,
  NavigationEnd,
  Params,
  Router,
} from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs';

import {
  BreadcrumbLabel,
  BreadcrumbSegment,
} from '../models/breadcrumb.model';
import { DynamicBreadcrumbService } from './dynamic-breadcrumb.service';
import { LanguageService } from './language.service';

/**
 * Builds the breadcrumb trail by walking the activated route tree root→leaf,
 * rather than having each route hardcode its full ancestor chain. A route
 * contributes one crumb by declaring `data: { breadcrumb: <label> }`; the URL
 * for that crumb is accumulated from the matched path segments. A route may also
 * declare `data: { breadcrumbLink: <url> }` to point its crumb at a DIFFERENT page
 * than its own path — needed when a route borrows a parent-feature label (e.g. a
 * dashboard drill-down whose "Dashboard" crumb must link to `/dashboard`, not to
 * the drill-down's own URL).
 *
 * A route may instead declare `data: { breadcrumbTrailKey: <key> }` to yield a
 * DATA-driven sub-trail: when {@link DynamicBreadcrumbService} holds a non-empty
 * trail for that key, those segments are spliced in place of the route's single
 * crumb (e.g. the Critical Documents folder hierarchy, known only from the API).
 * If no trail is published, the route's own `breadcrumb` is used as a fallback.
 *
 * Exposed as a signal (`computed`) so consumers bind directly under OnPush.
 * The trail is published asynchronously (after the API returns), so the computed
 * recomputes on `NavigationEnd` (via `navTick`), when the published segments change
 * (the `dynamic.get` read inside `build`), and on a language switch (`LanguageService.current`)
 * — labels are resolved through `TranslateService` (see `resolveLabel`).
 */
@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
  private readonly router = inject(Router);
  private readonly dynamic = inject(DynamicBreadcrumbService);
  private readonly translate = inject(TranslateService);
  private readonly language = inject(LanguageService);

  /** Bumped on every `NavigationEnd` so the `breadcrumbs` computed re-runs. */
  private readonly navTick = signal(0);

  /** Current breadcrumb trail; recomputes on navigation, on trail changes and on a
      language switch (labels are resolved through the translate runtime — see
      `resolveLabel`). */
  readonly breadcrumbs = computed<BreadcrumbSegment[]>(() => {
    this.navTick();
    this.language.current();
    return this.build();
  });

  /** Subscribe once (for the service's root lifetime) to `NavigationEnd` so the
   *  `breadcrumbs` computed re-runs on every completed navigation. */
  constructor() {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.navTick.update((n) => n + 1));
  }

  /**
   * Walk from the route-state root to the leaf, accumulating the URL and emitting
   * crumbs. For a route declaring `breadcrumbTrailKey` with a published trail whose
   * deepest crumb matches the CURRENT url (guarding against a stale/late publish),
   * splice those segments in (copied, `labelKey`s translated); otherwise emit the route's own
   * `breadcrumb`.
   * Reads `routeConfig.data` (not merged snapshot data) so `emptyOnly` inheritance
   * can't duplicate a parent crumb onto a path-less index child.
   */
  private build(): BreadcrumbSegment[] {
    const segments: BreadcrumbSegment[] = [];
    let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    let url = '';

    while (route) {
      const path = route.url.map((s) => s.path).join('/');
      if (path) {
        url += '/' + path;
      }

      const trailKey = route.routeConfig?.data?.['breadcrumbTrailKey'] as
        | string
        | undefined;
      const trail = trailKey ? this.dynamic.get(trailKey) : [];

      // Splice the published trail ONLY when it describes the CURRENT route — its
      // deepest crumb must point at the folder we're on (`url`). The trail is stored
      // globally by key and published by a root-scoped store, so a late async publish
      // (after the user navigated) or a prior folder's trail left over from a failed
      // refresh can otherwise render an unrelated folder's hierarchy. Validating the
      // deepest crumb against the current URL here means a stale/mismatched trail is
      // ignored (the route's own fallback crumb shows) rather than shown as truth —
      // a single consumer-side guard that neutralises the publisher-side races.
      const trailMatchesRoute =
        trail.length > 0 && trail[trail.length - 1].link === (url || '/');

      if (trailMatchesRoute) {
        // Build a fresh segment per stored one — build() later nulls the deepest crumb's
        // link, which must not mutate the segments held in DynamicBreadcrumbService. A
        // `labelKey` segment (UI copy) is resolved HERE, on every rebuild, so it follows a
        // language switch; a `label` segment (raw API data) is shown as-is.
        for (const s of trail) {
          const segment: BreadcrumbSegment = {
            label: s.labelKey !== undefined ? this.translate.instant(s.labelKey) : s.label,
            link: s.link,
          };
          if (s.queryParams) {
            segment.queryParams = s.queryParams;
          }
          segments.push(segment);
        }
      } else {
        const declared = route.routeConfig?.data?.['breadcrumb'] as
          | BreadcrumbLabel
          | undefined;
        if (declared !== undefined) {
          const label = this.resolveLabel(declared, route);
          if (label) {
            // A route whose crumb borrows a label pointing at a DIFFERENT page
            // (e.g. a dashboard drill-down showing a "Dashboard" parent crumb)
            // must override the link — the accumulated `url` is this route's own
            // path, so without the override the crumb links back to the current
            // page instead of the page it names. `data.breadcrumbLink` wins over
            // the accumulated URL when present.
            const linkOverride = route.routeConfig?.data?.['breadcrumbLink'] as
              | string
              | undefined;
            segments.push({
              label,
              link: linkOverride ?? (url || '/'),
              queryParams: this.keptQueryParams(route),
            });
          }
        }
      }

      route = route.firstChild;
    }

    /* The deepest crumb is the current page — never a link. */
    if (segments.length > 0) {
      segments[segments.length - 1].link = null;
    }

    return segments;
  }

  /**
   * Resolve a declared label to display text (see `BreadcrumbLabel`). A plain string is a
   * `breadcrumbs.*` dictionary key, resolved through `TranslateService.instant()` — an
   * unknown key is echoed back unchanged, so a route that still carries a literal (there
   * should be none after Task 6) degrades to showing that literal rather than throwing.
   * `':param'` labels and a function label's STRING result are runtime data (route params /
   * resolved data) and are NOT translated; a function label returning `{ labelKey }` is UI copy
   * (e.g. the "Folder" / "Document" fallbacks) and IS translated.
   */
  private resolveLabel(
    label: BreadcrumbLabel,
    route: ActivatedRouteSnapshot,
  ): string {
    if (typeof label === 'function') {
      const resolved = label(route);
      return typeof resolved === 'string' ? resolved : this.translate.instant(resolved.labelKey);
    }
    if (label.startsWith(':')) {
      const key = label.slice(1);
      const fromParams = route.params[key];
      const fromData = route.data[key];
      if (typeof fromParams === 'string') {
        return fromParams;
      }
      if (typeof fromData === 'string') {
        return fromData;
      }
      return '';
    }
    return this.translate.instant(label);
  }

  /**
   * Collects the query params a route asks to keep on its crumb link (via
   * `data.breadcrumbKeepQueryParams: string[]`), from the current navigation's
   * query params. Returns `undefined` when none are declared / present.
   */
  private keptQueryParams(route: ActivatedRouteSnapshot): Params | undefined {
    const keys = route.routeConfig?.data?.['breadcrumbKeepQueryParams'] as
      | string[]
      | undefined;
    if (!keys?.length) {
      return undefined;
    }
    const params: Params = {};
    for (const key of keys) {
      const value = route.queryParamMap.get(key);
      if (value != null) {
        params[key] = value;
      }
    }
    return Object.keys(params).length ? params : undefined;
  }
}
